import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT_EMAIL } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  organization: "",
  message: "",
};

export const ContactCTA = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please provide your name, email, and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. Duneworth will be in touch.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-10 bg-accent" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
                Partners · Investors · Operators
              </span>
            </div>
            <h2
              data-testid="contact-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance"
            >
              For partners, investors, and operators.
            </h2>
            <p
              data-testid="contact-body"
              className="mt-8 text-lg text-muted-foreground leading-relaxed text-pretty max-w-xl"
            >
              Duneworth is built for long-term value creation. We welcome
              conversations with strategic partners, investors, operators, and
              category experts aligned by the same horizon — equity, permanence,
              and disciplined ownership.
            </p>

            <div className="mt-12 space-y-4">
              <div className="border-t border-border pt-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-primary/60 mb-2">
                  Direct
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-email-link"
                  className="font-serif text-2xl sm:text-3xl text-primary hover:text-accent transition-colors duration-300"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary/55 pt-2">
                Selective · Disciplined · Long-term
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="border border-border bg-card p-8 sm:p-12 flex flex-col gap-8"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-8">
                <Field
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  testId="contact-input-name"
                  required
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  testId="contact-input-email"
                  required
                />
              </div>
              <Field
                label="Organization"
                name="organization"
                value={form.organization}
                onChange={handleChange}
                testId="contact-input-organization"
              />
              <FieldArea
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                testId="contact-input-message"
                required
              />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                <p className="text-xs text-muted-foreground">
                  Inquiries are reviewed personally. Expect a considered reply.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/85 disabled:opacity-60 disabled:cursor-not-allowed px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
                >
                  {submitting ? "Sending…" : "Contact Duneworth"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testId }) => (
  <label className="flex flex-col gap-3">
    <span className="text-[11px] uppercase tracking-[0.3em] text-primary/70 font-semibold">
      {label}
      {required && <span className="text-accent ml-1">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="bg-transparent border-0 border-b border-border focus:border-primary outline-none py-3 text-base text-primary placeholder:text-primary/30 transition-colors duration-300"
      autoComplete="off"
    />
  </label>
);

const FieldArea = ({ label, name, value, onChange, required, testId }) => (
  <label className="flex flex-col gap-3">
    <span className="text-[11px] uppercase tracking-[0.3em] text-primary/70 font-semibold">
      {label}
      {required && <span className="text-accent ml-1">*</span>}
    </span>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      rows={5}
      data-testid={testId}
      className="bg-transparent border-0 border-b border-border focus:border-primary outline-none py-3 text-base text-primary placeholder:text-primary/30 resize-none transition-colors duration-300"
    />
  </label>
);

export default ContactCTA;
